import { watch } from "vue";
import { useRoute } from "vue-router";
import { useUserStore } from "@/store/user";
import { useTempRecordStore, TempRecordType } from "@/store/tempRecord";
import { useCurrentPowerActivityRights } from "@/hooks/useActivityRightsByType";
import { use618UserSegment } from "@/views/power/hooks/use618UserSegment";
import { isActivity618DebugEnabled } from "@/utils/activity618Debug";
import { ACTIVITY_TIME_CONFIG } from "@/constants/activityTime";
import { $bus } from "@/mitt";
import { closeModalById, openModal } from "@/utils/modal";
import NewOrExpiredUserModal from "@/components/GlobleModal/Promotion618/NewOrExpiredUserModal.vue";
import ActiveMemberModal from "@/components/GlobleModal/Promotion618/ActiveMemberModal.vue";
import { PROMOTION_618_RECORD_KEY } from "@/components/GlobleModal/Promotion618/constants";
import type { Component } from "vue";
import type { UserSegment } from "@/views/power/components/VipPackageActivity/types";

let hasShownInSession = false;
let hasNotifiedClosed = false;
let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

function isImportantPopupExist(): boolean {
  return document.querySelector(".important-popup") !== null;
}

function notifyPromotion618Closed() {
  if (hasNotifiedClosed) return;
  hasNotifiedClosed = true;
  $bus.emit("promotion618Closed");
}

function resolveModalBySegment(segment: UserSegment): Component | null {
  if (segment === "activeVipSvip") {
    return ActiveMemberModal;
  }

  if (segment === "neverPaid" || segment === "expired") {
    return NewOrExpiredUserModal;
  }

  return null;
}

/**
 * 618 活动弹窗编排：站内信之后、营销弹窗之前，每日一次按用户分层展示。
 */
export function use618PromotionPopup() {
  const route = useRoute();
  const userStore = useUserStore();
  const tempRecordStore = useTempRecordStore();
  const { isAvailable: is618Active } = useCurrentPowerActivityRights();
  const { segment, is618Enabled } = use618UserSegment();

  /** 逐条检查弹窗展示条件，返回第一个不满足的原因；全部满足时返回 null */
  function getSkipReason(): string | null {
    if (hasShownInSession) return "本次会话已展示过";
    if (!userStore.isLogin) return "用户未登录";
    if (userStore.isGroupLogin) return "团队登录不展示";
    if (route.name !== "workbench") return `当前路由非工作台(${String(route.name)})`;
    if (!is618Active.value) return "活动未开启(服务端未下发或不在活动时间内)";
    if (!is618Enabled.value) return "Ultra用户不参与618活动";
    if (tempRecordStore.hasRecord(PROMOTION_618_RECORD_KEY)) return "今日已弹窗(每日限一次)";
    if (!resolveModalBySegment(segment.value)) return `无匹配弹窗类型(segment=${segment.value})`;
    return null;
  }

  function shouldShow(): boolean {
    return getSkipReason() === null;
  }

  /** 将 618 活动判定上下文打印到控制台，便于排查弹窗未弹/误弹等问题 */
  function log618Judgment(decision: "show" | "skip", skipReason: string | null) {
    const modalLabel = resolveModalBySegment(segment.value)
      ? segment.value === "activeVipSvip" ? "续费弹窗(ActiveMember)" : "新/过期用户弹窗(NewOrExpired)"
      : "无";

    const LABEL = "[618活动]";
    const style = decision === "show"
      ? "color:#22c55e;font-weight:bold"
      : "color:#ef4444;font-weight:bold";

    // console.group(`%c${LABEL} 弹窗判定 → ${decision === "show" ? "展示" : "跳过"}`, style);
    // console.table({
    //   "用户身份(segment)": segment.value,
    //   "最高套餐(highestPackage)": userStore.highestPackage,
    //   "是否会员(isMemberUser)": userStore.isMemberUser,
    //   "是否过期(isExpireUser)": userStore.isExpireUser,
    //   "活动期已购Pro月卡(activityPro)": userStore.sources.activityPro,
    //   "活动期已购Plus月卡(activityPlus)": userStore.sources.activityPlus,
    //   "活动是否开启(is618Active)": is618Active.value,
    //   "该用户启用618(is618Enabled)": is618Enabled.value,
    //   "调试模式(debugEnabled)": isActivity618DebugEnabled(),
    //   "活动时间窗口": `${ACTIVITY_TIME_CONFIG.start} ~ ${ACTIVITY_TIME_CONFIG.end}`,
    //   "已登录(isLogin)": userStore.isLogin,
    //   "团队登录(isGroupLogin)": userStore.isGroupLogin,
    //   "当前路由(route)": route.name ?? "—",
    //   "本次会话已弹过": hasShownInSession,
    //   "今日弹窗记录": tempRecordStore.hasRecord(PROMOTION_618_RECORD_KEY),
    //   "匹配弹窗类型": modalLabel,
    //   "判定结果": decision === "show" ? "展示弹窗" : `跳过(${skipReason})`
    // });
    // console.groupEnd();
  }

  function showPopup() {
    const skipReason = getSkipReason();
    if (skipReason) {
      log618Judgment("skip", skipReason);
      notifyPromotion618Closed();
      return;
    }

    if (isImportantPopupExist()) {
      log618Judgment("skip", "页面已有高优先级弹窗(important-popup)");
      return;
    }

    const ModalComponent = resolveModalBySegment(segment.value);
    if (!ModalComponent) {
      log618Judgment("skip", "resolveModalBySegment返回null(理论上不应到达)");
      notifyPromotion618Closed();
      return;
    }

    log618Judgment("show", null);

    hasShownInSession = true;
    tempRecordStore.setRecord(
      PROMOTION_618_RECORD_KEY,
      { segment: segment.value },
      TempRecordType.DAILY_RESET
    );

    const modalId =
      ModalComponent === ActiveMemberModal ? ActiveMemberModal.modalId : NewOrExpiredUserModal.modalId;
    const props =
      ModalComponent === NewOrExpiredUserModal
        ? {
            segment: segment.value as "neverPaid" | "expired",
            onClose: () => {
              closeModalById(modalId);
              notifyPromotion618Closed();
            }
          }
        : {
            onClose: () => {
              closeModalById(modalId);
              notifyPromotion618Closed();
            }
          };

    openModal(modalId, {
      component: ModalComponent,
      props
    });
  }

  function scheduleFallback() {
    if (fallbackTimer) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }

    if (!userStore.isLogin || route.name !== "workbench" || !is618Active.value) {
      return;
    }

    fallbackTimer = setTimeout(() => {
      showPopup();
    }, 3000);
  }

  $bus.on("siteMessageClosed", showPopup);

  watch(
    () => ({
      isLogin: userStore.isLogin,
      isGroupLogin: userStore.isGroupLogin,
      routeName: route.name,
      is618Active: is618Active.value,
      userId: userStore.userInfo.uniqueId
    }),
    ({ isLogin, routeName, is618Active: active, userId }, prev) => {
      if (userId !== prev?.userId) {
        hasShownInSession = false;
        hasNotifiedClosed = false;
      }

      if (isLogin && routeName === "workbench" && active) {
        scheduleFallback();
        return;
      }

      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
    },
    { immediate: true }
  );
}
